import { PLAYBACK_SPEEDS } from "@/lib/timeline/playback";
import type { TimelineViewTransform } from "../ViewTransform/transform";
import { clampTimelineZoom } from "../WorldMetrics/metrics";

export const TIMELINE_SESSION_STORAGE_KEY = "timeline-session-v1";

/** Zoom, pan, and whether the locked camera is following or held still. */
export interface TimelineCameraFrame {
  zoom: number;
  panX: number;
  panY: number;
  /** Null while lock is auto-fitting. Set when the viewer wheel-zooms. */
  heldZoom: number | null;
  /** Viewer dragged the locked camera; keep this frame until they unlock. */
  panHeld: boolean;
}

export interface TimelineSessionSnapshot extends TimelineCameraFrame {
  bookId: string;
  chapterId: string;
  playheadMs: number;
  speed: number;
}

/** Universe keeps its own slot so a visit cannot overwrite the book's playhead. */
function sessionStorageKey(chapterId: string): string {
  return chapterId === "universe"
    ? `${TIMELINE_SESSION_STORAGE_KEY}:universe`
    : TIMELINE_SESSION_STORAGE_KEY;
}

function clampSpeed(speed: number): number {
  return PLAYBACK_SPEEDS.includes(speed as (typeof PLAYBACK_SPEEDS)[number])
    ? speed
    : 1;
}

function parseView(value: unknown): TimelineViewTransform | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const v = value as Partial<TimelineViewTransform>;
  if (
    typeof v.zoom !== "number" ||
    typeof v.panX !== "number" ||
    typeof v.panY !== "number"
  ) {
    return null;
  }
  if (
    !Number.isFinite(v.zoom)
    || !Number.isFinite(v.panX)
    || !Number.isFinite(v.panY)
  ) {
    return null;
  }
  return {
    zoom: clampTimelineZoom(v.zoom),
    panX: v.panX,
    panY: v.panY,
  };
}

function parseHeldZoom(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return clampTimelineZoom(value);
}

export function readTimelineSession(
  bookId: string,
  chapterId: string,
): TimelineSessionSnapshot | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    const raw = localStorage.getItem(sessionStorageKey(chapterId));
    if (raw === null) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<TimelineSessionSnapshot>;
    if (parsed.bookId !== bookId || parsed.chapterId !== chapterId) {
      return null;
    }
    const view = parseView({
      zoom: parsed.zoom,
      panX: parsed.panX,
      panY: parsed.panY,
    });
    if (view === null || typeof parsed.playheadMs !== "number") {
      return null;
    }
    return {
      bookId,
      chapterId,
      playheadMs: Math.max(0, parsed.playheadMs),
      speed: clampSpeed(parsed.speed ?? 1),
      ...view,
      heldZoom: parseHeldZoom(parsed.heldZoom),
      panHeld: parsed.panHeld === true,
    };
  } catch {
    return null;
  }
}

export function writeTimelineSession(snapshot: TimelineSessionSnapshot): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.setItem(
    sessionStorageKey(snapshot.chapterId),
    JSON.stringify({
      bookId: snapshot.bookId,
      chapterId: snapshot.chapterId,
      playheadMs: Math.max(0, snapshot.playheadMs),
      speed: clampSpeed(snapshot.speed),
      zoom: snapshot.zoom,
      panX: snapshot.panX,
      panY: snapshot.panY,
      heldZoom: parseHeldZoom(snapshot.heldZoom),
      panHeld: snapshot.panHeld === true,
    }),
  );
}
